<template>
  <v-form :disabled="disabled">
    <v-card :outlined="outlined">
      <v-card-title v-if="isEpsonRoute" class="py-1">
        <DepartmentInput
          :vid="getVID('deptCd')"
          v-model="deptCdComputed"
          :validOnly="canEditDept()"
          :disabled="!canEditDept()"
          required
        />
        <v-spacer />
        <TextFieldRef v-if="getRouteJudge" :label="$t('ELE0181')" :value="getRouteJudge" />
      </v-card-title>
      <v-card-title v-else class="py-1">
        {{ routeTitle }}
        <v-spacer />
        <TextFieldRef v-if="getRouteJudge" :label="$t('ELE0181')" :value="getRouteJudge" />
      </v-card-title>
      <v-container fluid>
        <v-row>
          <v-col>
            <v-card outlined>
              <v-data-table
                :headers="headersRoute"
                :items="checkersComputed"
                dense
                disable-pagination
                hide-default-footer
                mobile-breakpoint="0"
              >
                <template v-slot:[`item.by`]="{ item }">
                  {{ getLabelBy(item.checkedCat) }}
                </template>
                <template v-slot:[`item.checkerCd`]="{ item, index }">
                  <UserInput v-if="!canEditChecker(index)" label="" v-model="item.checkerCd" disabled />
                  <UserInput
                    v-else
                    :vid="getDtlVID('checkerCd', index)"
                    label=""
                    v-model="item.checkerCd"
                    :companyCd="checkerCompanyCd"
                    :companyCat="companyCatCd"
                    :roleCdList="getRoleCdList(item.checkedCat)"
                    :exceptCdList="selectedUsers"
                    :required="item.checkedCat != enumSaWfCheckedCat.CHECK"
                    validOnly
                  />
                </template>
                <template v-slot:[`item.checkedDatetime`]="{ item }">
                  {{ toDate(item.checkedDatetime) }}
                </template>
                <template v-slot:[`item.remarks`]="{ item, index }">
                  <TextFieldRef v-if="!canEditChecker(index)" label="" :value="item.remarks" />
                  <TextFieldInput v-else label="" v-model="item.remarks" :maxLength="1000" />
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>
        <v-row v-if="isEpsonRoute">
          <v-col class="px-2 py-2">
            <v-card class="pb-2" outlined>
              <v-card-title class="py-1">
                {{ $t('ELE0245') }}
                <v-spacer />
                <BtnAdd :disabled="!canEditExamination()" @click="onAddExamination" />
              </v-card-title>
              <v-card class="mx-2" outlined>
                <v-data-table
                  :headers="headersExamination"
                  :items="evidencesComputed"
                  dense
                  disable-pagination
                  hide-default-footer
                  mobile-breakpoint="0"
                >
                  <template v-slot:[`item.action`]="{ index }">
                    <IconDelete :disabled="!canEditExamination()" @click="onDeleteExamination(index)" />
                  </template>
                  <template v-slot:[`item.checkedDatetime`]="{ item }">
                    {{ toDate(item.checkedDatetime) }}
                  </template>
                  <template v-slot:[`item.docNo`]="{ item, index }">
                    <TextFieldRef v-if="!canEditExamination(index)" label="" :value="item.docNo" />
                    <TextFieldInput
                      v-else
                      :vid="getDtlVID('docNo', index)"
                      label=""
                      v-model="item.docNo"
                      rules="halfWidth"
                      :maxLength="20"
                      required
                    />
                  </template>
                  <template v-slot:[`item.file`]="{ item, index }">
                    <FileInput
                      :vid="getDtlVID('filePath', index)"
                      label=""
                      v-model="item.file"
                      :filePath="item.filePath"
                      required
                    />
                  </template>
                </v-data-table>
              </v-card>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-card>
  </v-form>
</template>

<script>
import { SaWfCheckedCat, Role, CompanyCatCd } from '@/enum'
import DateUtils from '@/mixin/date-utils.js'

import TextFieldInput from '@/components/atoms/base/text-field/TextFieldInput'
import TextFieldRef from '@/components/atoms/base/text-field/TextFieldRef'
import BtnAdd from '@/components/atoms/button/BtnAdd'
import IconDelete from '@/components/atoms/icon/IconDelete.vue'
import FileInput from '@/components/atoms/file/FileInput'

import DepartmentInput from '@/components/atoms/select/DepartmentInput'
import UserInput from '@/components/atoms/select/UserInput'

export default {
  name: 'SaRequestRouteForm',

  mixins: [DateUtils],

  components: {
    TextFieldInput,
    TextFieldRef,
    BtnAdd,
    IconDelete,
    FileInput,
    DepartmentInput,
    UserInput,
  },

  props: {
    routeTitle: {
      type: String,
      required: false,
      default: function () {
        return this.$t('ELE0233')
      },
    },
    outlined: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    isEpsonRoute: {
      type: Boolean,
      required: false,
      default: false,
    },
    companyCd: {
      type: String,
      required: false,
      default: '',
    },
    companyCatCd: {
      type: String,
      required: true,
    },
    currentRouteNo: {
      type: Number,
      required: false,
      default: null,
    },
    currentRouteDtlNo: {
      type: Number,
      required: false,
      default: null,
    },
    routeNo: {
      type: Number,
      required: true,
    },
    deptCd: {
      type: String,
      required: false,
      default: '',
    },
    checkers: {
      type: Array,
      required: true,
    },
    evidences: {
      type: Array,
      required: false,
      default: () => [],
    },
  },

  data() {
    return {
      headersRoute: [
        { text: '', value: 'by', divider: true, sortable: false, cellClass: 'py-1' },
        {
          text: this.$t('ELE0058'),
          value: 'checkerCd',
          divider: true,
          sortable: false,
          cellClass: 'py-1',
        },
        {
          text: this.$t('ELE0234'),
          value: 'checkedDatetime',
          divider: true,
          sortable: false,
          cellClass: 'py-1',
        },
        { text: this.$t('ELE0054'), value: 'remarks', sortable: false, cellClass: 'py-1' },
      ],
      headersExamination: [
        { text: '', value: 'action', divider: true, sortable: false, cellClass: 'py-1', width: '40' },
        {
          text: this.$t('ELE0243'),
          value: 'checkedDatetime',
          divider: true,
          sortable: false,
          cellClass: 'py-1',
          width: '150',
        },
        { text: this.$t('ELE0244'), value: 'docNo', divider: true, sortable: false, cellClass: 'py-1', width: '300' },
        { text: this.$t('ELE0035'), value: 'file', sortable: false, cellClass: 'py-1' },
      ],
    }
  },

  computed: {
    enumSaWfCheckedCat: () => SaWfCheckedCat,
    selectedUsers: {
      // ユーザープルダウンで選択済みの値リスト
      get() {
        let items = []
        for (let idx = 0; idx < this.checkersComputed.length; idx++) {
          items.push({ userCd: this.checkersComputed[idx].checkerCd })
        }
        return items
      },
    },
    checkerCompanyCd: {
      get() {
        let companyCd = this.companyCd
        if (this.companyCatCd == CompanyCatCd.EPSON) {
          companyCd = ''
        }
        return companyCd
      },
    },
    deptCdComputed: {
      get() {
        return this.deptCd
      },
      set(value) {
        this.$emit('update:deptCd', value)
      },
    },
    checkersComputed: {
      get() {
        return this.checkers
      },
      set(value) {
        this.$emit('update:checkers', value)
      },
    },
    evidencesComputed: {
      get() {
        return this.evidences
      },
      set(value) {
        this.$emit('update:evidences', value)
      },
    },
    getRouteJudge: {
      get() {
        let result = ''
        if (!this.currentRouteNo || this.routeNo < this.currentRouteNo) {
          for (let i = this.checkers.length - 1; i >= 0; i--) {
            const checker = this.checkers[i]
            if (checker.result != null) {
              result = checker.result ? this.$t('ELE0264') : this.$t('ELE0265')
              break
            }
          }
        }
        return result
      },
    },
  },

  methods: {
    canEditDept() {
      let result = true

      if (this.disabled) {
        result = false
      } else if (this.isCurrentRoute() && this.currentRouteDtlNo > 1) {
        result = false
      } else {
        result = true
      }
      return result
    },
    canEditChecker(index) {
      let result = true

      if (this.disabled) {
        result = false
      } else if (this.routeNo < this.currentRouteNo) {
        result = false
      } else if (this.isCurrentRoute() && index + 1 < this.currentRouteDtlNo) {
        result = false
      } else {
        result = true
      }
      return result
    },
    canEditExamination() {
      let result = true

      if (this.disabled) {
        result = false
      } else if (!this.isCurrentRoute()) {
        result = false
      } else {
        result = true
      }
      return result
    },
    isCurrentRoute() {
      return this.routeNo == this.currentRouteNo
    },
    toDate(datetime) {
      return this.toLocalDate(datetime)
    },
    getLabelBy(checkedCat) {
      let label = ''
      if (checkedCat == SaWfCheckedCat.REQUEST) {
        label = this.$t('ELE0235')
      } else if (checkedCat == SaWfCheckedCat.CHECK) {
        label = this.$t('ELE0236')
      } else if (checkedCat == SaWfCheckedCat.APPROVE) {
        label = this.$t('ELE0237')
      }
      return label
    },
    getRoleCdList(checkedCat) {
      let list = []
      if (checkedCat == SaWfCheckedCat.CHECK) {
        list = [Role.SA_CHECK]
      } else if (checkedCat == SaWfCheckedCat.APPROVE) {
        list = [Role.SA_APPROVE]
      } else {
        if (this.isEpsonRoute) {
          list = [Role.SA_CHECK, Role.SA_APPROVE]
        } else {
          list = []
        }
      }
      return list
    },
    getVID(name) {
      return name + '_' + this.routeNo
    },
    getDtlVID(name, idx) {
      return name + '_' + this.routeNo + '_' + idx
    },
    onAddExamination() {
      const item = {
        checkedDatetime: '',
        docNo: '',
        filePath: '',
        file: null,
      }
      this.evidencesComputed.push(item)
    },
    onDeleteExamination(index) {
      this.evidencesComputed.splice(index, 1)
    },
  },
}
</script>
